import { DetailedMatchResult, PlayerStatistics } from '../types';

export const aggregatePlayerStats = (matches: DetailedMatchResult[]): PlayerStatistics => {
    const heroStats: PlayerStatistics['heroStats'] = {};
    const heroMatchups: PlayerStatistics['heroMatchups'] = {};
    const classMatchups: PlayerStatistics['classMatchups'] = {};
    const heroVsClassMatchups: PlayerStatistics['heroVsClassMatchups'] = {};

    matches.forEach(m => {
        // We only aggregate matches that have the new detailed fields
        if (!m.myHero) return;

        const isWin = m.result === 'win';
        const myHero = m.myHero;
        const myClass = m.myClass;
        const oppHero = m.opponentHero;
        const oppClass = m.opponentClass;

        // 1. Hero Stats + Class Synergy
        if (!heroStats[myHero]) {
            heroStats[myHero] = { games: 0, wins: 0, winRate: 0, withClass: {} };
        }
        heroStats[myHero].games++;
        if (isWin) heroStats[myHero].wins++;

        if (myClass) {
            if (!heroStats[myHero].withClass[myClass]) {
                heroStats[myHero].withClass[myClass] = { games: 0, wins: 0, winRate: 0 };
            }
            heroStats[myHero].withClass[myClass].games++;
            if (isWin) heroStats[myHero].withClass[myClass].wins++;
        }

        // 2. Hero vs Hero Matchup
        if (oppHero) {
            const key = `${myHero} vs ${oppHero}`;
            if (!heroMatchups[key]) heroMatchups[key] = { wins: 0, total: 0, winRate: 0 };
            heroMatchups[key].total++;
            if (isWin) heroMatchups[key].wins++;
        }

        // 3. Class vs Class Matchup
        if (myClass && oppClass) {
            const key = `${myClass} vs ${oppClass}`;
            if (!classMatchups[key]) classMatchups[key] = { wins: 0, total: 0, winRate: 0 };
            classMatchups[key].total++;
            if (isWin) classMatchups[key].wins++;
        }

        // 4. Hero vs Class Matchup
        if (oppClass) {
            const key = `${myHero} vs ${oppClass}`;
            if (!heroVsClassMatchups[key]) heroVsClassMatchups[key] = { wins: 0, total: 0, winRate: 0 };
            heroVsClassMatchups[key].total++;
            if (isWin) heroVsClassMatchups[key].wins++;
        }
    });

    // Calculate Win Rates
    Object.values(heroStats).forEach(h => {
        h.winRate = h.games > 0 ? Math.round((h.wins / h.games) * 100) : 0;
        Object.values(h.withClass).forEach(c => {
            c.winRate = c.games > 0 ? Math.round((c.wins / c.games) * 100) : 0;
        });
    });

    const calcMapWinRate = (map: Record<string, { wins: number; total: number; winRate: number }>) => {
        Object.values(map).forEach(m => {
            m.winRate = m.total > 0 ? Math.round((m.wins / m.total) * 100) : 0;
        });
    };

    calcMapWinRate(heroMatchups);
    calcMapWinRate(classMatchups);
    calcMapWinRate(heroVsClassMatchups);

    return { heroStats, heroMatchups, classMatchups, heroVsClassMatchups };
};

export const getBestClass = (withClass: Record<string, { games: number; wins: number; winRate: number }>): string => {
    const sorted = Object.entries(withClass).sort((a, b) => b[1].winRate - a[1].winRate);
    return sorted[0] ? `${sorted[0][0]} (${sorted[0][1].winRate}%)` : 'N/A';
};

export const getWorstClass = (withClass: Record<string, { games: number; wins: number; winRate: number }>): string => {
    const sorted = Object.entries(withClass).sort((a, b) => a[1].winRate - b[1].winRate);
    return sorted[0] ? `${sorted[0][0]} (${sorted[0][1].winRate}%)` : 'N/A';
};
